<template>
  <!-- S02 · Datos del contrato: alcance + generales + económico / garantías / seguros. -->
  <div class="s02">
    <!-- Alcance / datos basicos del proyecto (manual): textarea siempre visible, como el diseño.
         Se refleja en solo lectura en el Dashboard. -->
    <summary-card :title="$t('prm_summary_s02_scope_title')" :mode="mode">
      <div class="scope-block">
        <span class="scope-label">{{ $t('prm_summary_s02_scope_label') }}</span>
        <textarea class="scope-area" :value="byPath('s02.scope') || ''"
                  :placeholder="$t('prm_summary_s02_scope_placeholder')"
                  @change="onSave('s02.scope', $event.target.value)"></textarea>
      </div>
    </summary-card>

    <summary-card :title="$t('prm_summary_s02_general')" :mode="mode">
      <kv-field v-for="f in generalFields" :key="f.key" :label="$t(f.key)" :source="f.source"
                :editable="!f.readonly" :value="f.value !== undefined ? f.value : byPath(f.path)" :format="f.format || null"
                :type="f.type || 'text'" :options="f.options || null" :allow-other="!!f.allowOther" :block="!!f.block"
                :mode="mode" @save="onSave(f.path, $event)"></kv-field>
      <!-- Nomenclatura / concepto del contrato: nombre que se da al contrato + documento adjunto (versionado). -->
      <kv-field :label="$t('prm_summary_s02_contract_name')" :editable="true" :value="byPath('s02.contractName') || ''"
                source="cm" :mode="mode" @save="onSave('s02.contractName', $event)">
        <template #extra>
          <prm-versioned-file :file="contractDocFile" :editing="true" :project-id="projectId"
            :open="docHistoryOpen" :upload-url="contractDocUploadUrl"
            @uploaded="$emit('files-changed')" @toggle-history="docHistoryOpen = !docHistoryOpen"></prm-versioned-file>
        </template>
      </kv-field>
      <prm-file-history v-if="docHistoryOpen && contractDocFile" :file="contractDocFile"
        :base-url="baseUrl" :project-id="projectId" @close="docHistoryOpen = false"></prm-file-history>
    </summary-card>

    <!-- Fechas clave del proyecto: manuales (con motivo e historial) + reales de las actividades. -->
    <prm-s02-key-dates :key-dates="byPath('s02.keyDates') || {}" :auto-dates="sources.keyDates || {}"
        :revisions="byPath('s02.dateRevisions') || []" :mode="mode"
        @save-field="$emit('save-field', $event)" @save-list="$emit('save-list', $event)"></prm-s02-key-dates>

    <summary-card :title="$t('prm_summary_s02_economic')" :mode="mode">
      <!-- Moneda local: selector unico del proyecto. Al elegir EUR (divisa base) el cambio pasa a 1. -->
      <kv-field :label="$t('prm_summary_s02_local_currency')" :editable="true" :value="localCurrency"
                :options="currencyOptions" source="cm" :mode="mode" @save="onCurrencySave"></kv-field>
      <!-- Importe local (con simbolo de la moneda) y su equivalente en € (local x tipo de cambio). -->
      <!-- Importe del contrato en moneda local: manual (fuente única). Alimenta a Hitos y Finanzas. -->
      <kv-field :label="$t('prm_summary_s02_contract_amount_local')" :editable="true" :value="contractAmountLocal"
                :format="fmtLocalAmt" source="cm" :mode="mode" @save="onSave('s03.contractAmountLocal', $event)"></kv-field>
      <kv-field :label="$t('prm_summary_s02_contract_amount_eur')" :value="contractEur"
                :format="eur" source="calc" :mode="mode"></kv-field>

      <!-- Avales y Seguros: listas dinámicas con PDF versionado (tal cual el diseño). -->
      <prm-contract-list :title="$t('prm_summary_s02_guarantees')" :note="$t('prm_summary_s02_guarantees_note')"
          :add-label="$t('prm_summary_s02_guarantee_add')" :edit-label="$t('prm_summary_s02_guarantee_edit')"
          :item-label="$t('prm_summary_s02_guarantee_item')" :remove-label="$t('prm_summary_remove_row')"
          collection="guarantee" :columns="guaranteeColumns" :rows="byPath('s02.guarantees') || []"
          :files="contractFiles.guarantee" :project-id="projectId" :currency="localCurrency" :reset-key="resetKey"
          @save="(items) => onSaveList('s02.guarantees', items)"
          @files-changed="$emit('files-changed')" @remove-file="(id) => $emit('remove-file', id)">
        </prm-contract-list>

        <prm-contract-list :title="$t('prm_summary_s02_insurances')" :note="$t('prm_summary_s02_insurances_note')"
          :add-label="$t('prm_summary_s02_insurance_add')" :edit-label="$t('prm_summary_s02_insurance_edit')"
          :item-label="$t('prm_summary_s02_insurance_item')" :remove-label="$t('prm_summary_remove_row')"
          collection="insurance" :columns="insuranceColumns" :rows="byPath('s02.insurances') || []"
          :files="contractFiles.insurance" :project-id="projectId" :currency="localCurrency" :reset-key="resetKey"
          @save="(items) => onSaveList('s02.insurances', items)"
          @files-changed="$emit('files-changed')" @remove-file="(id) => $emit('remove-file', id)">
      </prm-contract-list>

      <kv-field v-for="f in economicFields" :key="f.key" :label="$t(f.key)" :source="f.source"
                :editable="!f.readonly" :value="f.value !== undefined ? f.value : byPath(f.path)" :format="f.format || null"
                :type="f.type || 'text'" :options="f.options || null" :block="!!f.block"
                :mode="mode" @save="onSave(f.path, $event)"></kv-field>
    </summary-card>
  </div>
</template>

<script>
  import SummaryCard from './summary-card.vue'
  import KvField from './kv-field.vue'
  import PrmContractList from './contract-list.vue'
  import PrmS02KeyDates from './s02-key-dates.vue'
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import { fmtEur, fmtLocalAmount } from '../shared/format'
  import { clientAirportOptions } from '../shared/airports'

  // Divisas locales soportadas (una por proyecto). EUR es la base: su tipo de cambio es 1.
  const CURRENCIES = ['EUR', 'USD', 'GBP', 'CAD', 'CHF', 'SEK']

  export default {
    name: 'PrmSummaryS02',

    components: { SummaryCard, KvField, PrmContractList, PrmS02KeyDates, PrmVersionedFile, PrmFileHistory },

    props: {
      doc: { type: Object, required: true },
      sources: { type: Object, required: true },
      project: { type: Object, default: () => ({}) }, // para las URLs de subida/descarga de PDFs
      mode: { type: Boolean, default: false },
      resetKey: { type: Number, default: 0 }, // sube al recargar -> re-inicializa las listas dinámicas
    },

    data () {
      return { docHistoryOpen: false } // historial del documento del contrato desplegado
    },

    computed: {
      generalFields () {
        return [
          // SAP / Nº proyecto: manual (fuente única del número). El Resumen (S01) lo lee de aquí.
          // Si aún no se ha rellenado, arranca con la referencia real del proyecto (editable).
          { key: 'prm_summary_sap', source: 'cm', path: 's01.sap', value: this.byPath('s01.sap') || this.sources.sapCode },
          { key: 'prm_summary_project_name', source: 'admin', readonly: true, value: this.sources.projectName },
          // Cliente: selector del catálogo de aeropuertos con un "Otros" al final que permite
          // escribir un nombre custom (texto libre).
          { key: 'prm_summary_client', source: 'cm', path: 's01.client', type: 'select', options: this.clientOptions, allowOther: true },
          // Tipo de contrato clasificado por tipo de precio y por tipo de cliente (sustituye al texto libre).
          { key: 'prm_summary_s02_price_type', source: 'cm', path: 's02.priceType', type: 'select', options: this.priceTypeOptions },
          { key: 'prm_summary_s02_client_type', source: 'cm', path: 's02.clientType', type: 'select', options: this.clientTypeOptions },
          { key: 'prm_summary_s02_incoterm', source: 'cm', path: 's02.incoterm', type: 'select', options: this.incotermOptions },
          { key: 'prm_summary_s02_contract_language', source: 'cm', path: 's02.contractLanguage' },
          { key: 'prm_summary_s02_other_languages', source: 'cm', path: 's02.otherLanguages' },
          { key: 'prm_summary_s02_applicable_law', source: 'cm', path: 's02.applicableLaw' },
          { key: 'prm_summary_s02_jurisdiction', source: 'cm', path: 's02.jurisdiction' },
          { key: 'prm_summary_s02_arbitration', source: 'cm', path: 's02.arbitration' },
          { key: 'prm_summary_s02_liability_limits', source: 'cm', path: 's02.liabilityLimits', block: true },
        ]
      },
      economicFields () {
        return [
          { key: 'prm_summary_s02_advance', source: 'cm', path: 's02.advance' },
          { key: 'prm_summary_s02_retentions', source: 'cm', path: 's02.retentions', block: true },
          { key: 'prm_summary_s02_price_review', source: 'cm', path: 's02.priceReview' },
          { key: 'prm_summary_s02_penalty_cap', source: 'cm', path: 'meta.penaltyCapPct', format: this.pctPlain, block: true },
          { key: 'prm_summary_s02_performance_damage', source: 'cm', path: 's02.performanceDamage', block: true },
          // Garantía y periodo (texto): garantía, su duración y la fecha de inicio de cómputo.
          { key: 'prm_summary_s02_warranty', source: 'cm', path: 's02.warranty', block: true },
          { key: 'prm_summary_s02_warranty_duration', source: 'cm', path: 's02.warrantyDuration' },
          { key: 'prm_summary_s02_warranty_start', source: 'cm', path: 's02.warrantyStart', type: 'date' },
        ]
      },
      // Tipo de precio del contrato (select con opción vacía para poder dejarlo sin marcar).
      priceTypeOptions () {
        return [{ value: '', label: '' }].concat(
          ['po', 'lump_sum', 'unit_price', 'cost_reimbursable', 'mixed']
            .map((v) => ({ value: v, label: this.$t('prm_summary_s02_price_type_' + v) })))
      },
      // Tipo de cliente (entidad pública, privada, intercompany, otros).
      clientTypeOptions () {
        return [{ value: '', label: '' }].concat(
          ['public', 'private', 'intercompany', 'other']
            .map((v) => ({ value: v, label: this.$t('prm_summary_s02_client_type_' + v) })))
      },
      // Incoterms 2020 (códigos estándar, sin traducción).
      incotermOptions () {
        const codes = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP']
        return [{ value: '', label: '' }].concat(codes.map((c) => ({ value: c, label: c })))
      },
      // Moneda local (una por proyecto) e importes del contrato. El € = local x tipo de cambio.
      localCurrency () { return this.byPath('s03.contractLocalCurrency') || 'EUR' },
      rate () {
        const r = Number(this.byPath('s03.exchangeRate'))
        return Number.isFinite(r) && r > 0 ? r : 1
      },
      contractAmountLocal () {
        const v = this.byPath('s03.contractAmountLocal')
        return (v === null || v === undefined) ? '' : v
      },
      contractEur () {
        const a = this.byPath('s03.contractAmountLocal')
        return (a === null || a === undefined || a === '') ? '' : (Number(a) || 0) * this.rate
      },
      currencyOptions () {
        return CURRENCIES.map((c) => ({ value: c, label: c + ' · ' + this.$t('prm_currency_' + c.toLowerCase()) }))
      },
      // Clientes: catálogo de aeropuertos de tkelevator + "Otros" al final (nombre custom).
      clientOptions () {
        return clientAirportOptions().concat([{ value: '__other__', label: this.$t('prm_summary_other') }])
      },
      // Proyecto y ficheros PDF (por fila) para avales/seguros.
      projectId () { return this.project && this.project.id },
      contractFiles () { return this.sources.contractFiles || { guarantee: {}, insurance: {} } },
      baseUrl () { return `/api/v2/project/${this.projectId}/dashboard-prm` },
      // Documento del contrato (concepto): un único fichero versionado, clave fija 'main'.
      contractDocFile () { return (this.contractFiles.contract_doc || {}).main || null },
      contractDocUploadUrl () { return `${this.baseUrl}/contract-files/contract_doc/main` },
      yesNoOptions () {
        return [{ value: 'yes', label: this.$t('prm_summary_yes') }, { value: 'no', label: this.$t('prm_summary_no') }]
      },
      // Catálogos de tipo (mismas opciones que el diseño).
      avalTypeOptions () {
        return ['advance', 'performance', 'warranty'].map((c) => ({ value: c, label: this.$t('prm_summary_s02_guarantee_type_' + c) }))
      },
      insuranceTypeOptions () {
        return ['pdbi', 'tpl', 'cargo', 'rca', 'ear', 'rcg'].map((c) => ({ value: c, label: this.$t('prm_summary_s02_insurance_type_' + c) }))
      },
      guaranteeColumns () {
        return [
          { key: 'type', label: 'prm_summary_s02_guarantee_col_type', type: 'select', options: this.avalTypeOptions, flex: 2 },
          { key: 'date', label: 'prm_summary_s02_guarantee_col_date', type: 'date', flex: 1 },
          { key: 'endDate', label: 'prm_summary_s02_guarantee_col_end_date', type: 'date', flex: 1 },
          { key: 'amount', label: 'prm_summary_s02_guarantee_col_amount', type: 'number', flex: 1 },
          { key: 'requirement', label: 'prm_summary_s02_guarantee_col_requirement', type: 'select', options: this.yesNoOptions, flex: 1 },
        ]
      },
      insuranceColumns () {
        return [
          { key: 'type', label: 'prm_summary_s02_insurance_col_type', type: 'select', options: this.insuranceTypeOptions, flex: 3 },
          { key: 'responsible', label: 'prm_summary_s02_insurance_col_responsible', type: 'select', options: this.insuranceResponsibleOptions, flex: 1 },
          { key: 'applies', label: 'prm_summary_s02_insurance_col_applies', type: 'select', options: this.yesNoOptions, flex: 1 },
          { key: 'contracted', label: 'prm_summary_s02_insurance_col_contracted', type: 'select', options: this.yesNoOptions, flex: 1 },
          { key: 'start', label: 'prm_summary_s02_insurance_col_start', type: 'date', flex: 1 },
          { key: 'end', label: 'prm_summary_s02_insurance_col_end', type: 'date', flex: 1 },
          // Prórroga: si renueva (Sí/No) y, en caso de renovar, la fecha de renovación.
          { key: 'renews', label: 'prm_summary_s02_insurance_col_renews', type: 'select', options: this.yesNoOptions, flex: 1 },
          { key: 'renewalDate', label: 'prm_summary_s02_insurance_col_renewal_date', type: 'date', flex: 1 },
        ]
      },
      // Responsable del seguro: TKAS o el Cliente (varios proyectos exigen seguros al cliente).
      insuranceResponsibleOptions () {
        return [
          { value: 'tkas', label: 'TKAS' },
          { value: 'client', label: this.$t('prm_summary_client') },
        ]
      },
    },

    methods: {
      byPath (path) {
        return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), this.doc)
      },
      onSave (path, value) {
        this.$emit('save-field', { path, value })
      },
      onSaveList (path, items) {
        this.$emit('save-list', { path, items })
      },
      // Cambia la moneda local (una por proyecto). EUR es la base: fija el tipo de cambio a 1.
      onCurrencySave (value) {
        this.onSave('s03.contractLocalCurrency', value)
        if (value === 'EUR') this.onSave('s03.exchangeRate', 1)
      },
      fmtLocalAmt (v) { return fmtLocalAmount(v, this.localCurrency) },
      eur (v) { return fmtEur(v) },
      pctPlain (v) {
        return v + ' %'
      },
    },
  }
</script>

<style lang="scss" scoped>
  .s02 { display: flex; flex-direction: column; gap: 10px; }

  // Alcance (S02): sublabel + textarea siempre visible, con el estilo del diseño (no el del Dashboard).
  .scope-block { padding: 10px; }
  .scope-label {
    display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em;
    color: #5a7896; margin-bottom: 6px;
  }
  .scope-area {
    width: 100%; min-height: 84px; resize: vertical; font-size: 12px; color: #2c3a4d; line-height: 1.5;
    border: 1px solid #d5dbe4; border-radius: 6px; background: #fff; padding: 8px 10px; outline: none;
  }
  .scope-area:focus { border-color: #3a6bc8; box-shadow: 0 0 0 2px rgba(58, 107, 200, .12); }
  .scope-area::placeholder { color: #9aa4b2; }
</style>
